/**
 * vl-home-product-cards.js — 首页「榜单产品卡横滑区」的胶囊切换
 *
 * 需求：需求/20260922-新版首页改版/子需求/06-首页榜单产品卡横滑区/
 *
 * 职责（用户 2026-09-30 裁决 P0-4：可滑动、不自动播放；2026-10-08 追加 PC 左右切换按钮）：
 *   ①胶囊（role=tab）点击切换榜单面板（不刷新页面、不改写 URL）；
 *   ②键盘：←/→ 在胶囊间移动并切换，Home/End 跳到首/末（role=tablist 的标准键盘交互）；
 *   ③面板显隐用 `hidden` + `.is-active`；`aria-selected` 与 `tabindex`（roving tabindex）同步。
 *   ④PC 左右切换按钮（`[data-vl-hpc-prev]` / `[data-vl-hpc-next]`，Figma `1967:12473`）：
 *     每次滚一整张卡（卡宽 + 轨道间距）并带缓动；按实际滚动位置刷新 `disabled`（到头即禁用，
 *     图标由 CSS 切到 Figma 的 #BBBBBE 禁用稿）；轨道不可滚动（卡数不足一屏）时整块隐藏。
 *     **M 端不显示**（CSS 在 <768 隐藏，用户 2026-10-08 口径）。
 *   **不做**：自动播放、进度指示；横向滚动仍依赖原生 overflow-x（按钮只是程序化滚动它）。
 *
 * 约定（规范 3.6 / 与 vl-quiz-category-cards.js 同风格）：
 *   IIFE + 只注册一次自定义元素 + `disconnectedCallback` 里摘掉监听。
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || window.VLHomeProductCards) return;

  var KEYS = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
  var PAGE_MS = 300; /* 单次箭头翻页的缓动时长（比整轮换卡短，点按更跟手） */

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /*
   * 首页卡信息条跑马灯：实现已抽到 assets/vl-info-marquee.js（2026-10-29）。
   * 保留本地函数名，调用点不变；脚本未加载时静默跳过。
   */
  function scheduleInfoMarquees(root) {
    if (window.VLInfoMarquee) window.VLInfoMarquee.schedule(root);
  }

  /*
   * 活动信息条槽位：2026-10-29（方案 E）改由 CSS 处理 —— 面板级 `:has` 给
   * `--vl-info-slot-h`（32 / 61 / PC 66），不再需要 JS 逐面板测量高度。
   */

  class VLHomeProductCards extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this._tabs = Array.prototype.slice.call(this.querySelectorAll('[data-vl-hpc-tab]'));
      this._panels = Array.prototype.slice.call(this.querySelectorAll('[data-vl-hpc-panel]'));

      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      this._bindRails();
      scheduleInfoMarquees(this);
    }

    disconnectedCallback() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
      if (this._onKeydown) this.removeEventListener('keydown', this._onKeydown);
      this._unbindRails();
      this._bound = false;
    }

    _handleClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      /* 左右切换按钮：滚一条轨道（不改变当前榜单） */
      var nav = target.closest('[data-vl-hpc-prev], [data-vl-hpc-next]');
      if (nav && this.contains(nav)) {
        var panel = typeof nav.closest === 'function' ? nav.closest('.vl-hpc__panel') : null;
        var entry = this._entryFor(panel);
        if (entry) this._page(entry, nav.hasAttribute('data-vl-hpc-next') ? 1 : -1);
        return;
      }

      var tab = target.closest('[data-vl-hpc-tab]');
      if (!tab || !this.contains(tab)) return;
      this._select(parseInt(tab.getAttribute('data-vl-hpc-tab'), 10));
    }

    _handleKeydown(event) {
      if (this._tabs.length < 2) return; /* 单个榜单：不拦截方向键，留给原生轨道滚动 */
      var step = KEYS[event.key];
      if (!step) return;

      var current = this._tabs.indexOf(document.activeElement);
      if (current < 0) return;

      var next;
      if (step === 'first') next = 0;
      else if (step === 'last') next = this._tabs.length - 1;
      else next = (current + step + this._tabs.length) % this._tabs.length;

      event.preventDefault();
      this._select(next);
      this._tabs[next].focus();
    }

    _select(index) {
      if (isNaN(index) || index < 0 || index >= this._tabs.length) return;

      this._tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.classList.toggle('is-selected', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });

      this._panels.forEach(function (panel) {
        var active = parseInt(panel.getAttribute('data-vl-hpc-panel'), 10) === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        // 切换回榜单时把轨道滚回开头，避免"上一个榜单滚到中间"的状态残留
        if (active) {
          var rail = panel.querySelector('.vl-hpc__rail');
          if (rail) rail.scrollLeft = 0;
        }
      });

      // 面板显隐会改变轨道的可滚动量（隐藏时量不到尺寸）⇒ 切换后重算左右按钮
      if (this._rails) {
        this._rails.forEach(function (entry) {
          entry.stops = null; // 面板从 hidden 变可见后尺寸才可测 ⇒ 缓存失效
        });
      }
      this._syncAllNav();
      scheduleInfoMarquees(this);
    }

    /* ------------------------------------------------------------- 左右切换按钮（PC） */

    /* 绑定每条轨道的滚动监听（按钮的点击走宿主的委托监听） */
    _bindRails() {
      var self = this;
      this._rails = Array.prototype.slice.call(this.querySelectorAll('.vl-hpc__rail')).map(function (rail) {
        var panel = typeof rail.closest === 'function' ? rail.closest('.vl-hpc__panel') : null;
        var nav = panel ? panel.querySelector('[data-vl-hpc-nav]') : null;
        var entry = {
          rail: rail,
          panel: panel,
          nav: nav,
          prev: nav ? nav.querySelector('[data-vl-hpc-prev]') : null,
          next: nav ? nav.querySelector('[data-vl-hpc-next]') : null,
          onScroll: null
        };
        if (nav) {
          entry.onScroll = function () {
            self._syncNav(entry);
          };
          rail.addEventListener('scroll', entry.onScroll, { passive: true });
        }
        return entry;
      });

      this._onResize = function () {
        if (self._resizeTimer) window.clearTimeout(self._resizeTimer);
        self._resizeTimer = window.setTimeout(function () {
          if (self._rails) {
            self._rails.forEach(function (entry) {
              entry.stops = null; /* 视口变化 ⇒ 页位置缓存失效 */
            });
          }
          self._syncAllNav();
        }, 150);
      };
      window.addEventListener('resize', this._onResize);

      this._syncAllNav();
    }

    _unbindRails() {
      if (this._rails) {
        this._rails.forEach(function (entry) {
          if (entry.onScroll) entry.rail.removeEventListener('scroll', entry.onScroll);
        });
        this._rails = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._resizeTimer) {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = 0;
      }
      if (this._scrollRaf) {
        window.cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = 0;
      }
    }

    _entryFor(panel) {
      if (!panel || !this._rails) return null;
      for (var i = 0; i < this._rails.length; i++) {
        if (this._rails[i].panel === panel) return this._rails[i];
      }
      return null;
    }

    _syncAllNav() {
      var self = this;
      if (!this._rails) return;
      this._rails.forEach(function (entry) {
        self._syncNav(entry);
      });
    }

    /* 按滚动位置刷新可用态：处在第一页禁用 prev、处在最后一页禁用 next；不足一页则整块隐藏 */
    _syncNav(entry) {
      if (!entry.nav) return;
      var stops = this._pageStops(entry);
      if (stops.length < 2) {
        entry.nav.classList.add('is-static');
        return;
      }
      entry.nav.classList.remove('is-static');
      var i = this._stopIndex(entry.rail.scrollLeft, stops);
      if (entry.prev) entry.prev.disabled = i <= 0;
      if (entry.next) entry.next.disabled = i >= stops.length - 1;
    }

    /* 翻页：滚到相邻的「页位置」——一张卡一页（卡宽 + 轨道间距） */
    _page(entry, dir) {
      var stops = this._pageStops(entry);
      if (stops.length < 2) return;
      var i = this._stopIndex(entry.rail.scrollLeft, stops);
      var next = Math.min(Math.max(0, i + dir), stops.length - 1);
      if (next === i) return; /* 已在端点：不产生"几像素"的空位移 */
      this._scrollTo(entry.rail, stops[next], prefersReducedMotion());
    }

    /*
     * 页位置（scrollLeft 的离散取值），带缓存（滚动中每帧都会问，避免反复触发布局）：
     *   ①一张卡一页：0, step, 2·step …
     *   ②末尾不足**半张卡**的余量**并入上一页**，并把该页直接对齐到真实末尾（max）。
     *     这样每一页的位移都 ≥ 半张卡，且末尾一定能滚到（上一页的目标被替换为 max，不丢内容）。
     *   例：4 张卡（PC 1440）= 内容 1608 - 可见 1200 ⇒ max 408、step 406 ⇒ [0, 408]（2 页），
     *       修掉用户 2026-10-08 反馈的"第二次点击只挪 2px"。
     *   ③可滚动量不足半张卡 ⇒ 只有 [0]（视为不可滚动 → 浮层整块隐藏）。
     * 失效时机：窗口 resize、榜单切换（面板从 hidden 变可见时尺寸才可测）。
     */
    _pageStops(entry) {
      if (entry.stops) return entry.stops;
      var rail = entry.rail;
      var step = this._step(rail);
      var max = Math.round(Math.max(0, rail.scrollWidth - rail.clientWidth));
      var stops = [0];
      if (step > 0 && max > 0 && max >= step / 2) {
        var k = 1;
        while (k < 200) {
          var t = Math.min(k * step, max);
          if (t >= max) {
            if (t - stops[stops.length - 1] >= step / 2) stops.push(max);
            else stops[stops.length - 1] = max; /* 末尾余量并入上一页，并对齐真实末尾 */
            break;
          }
          stops.push(t);
          k++;
        }
      }
      entry.stops = stops;
      return stops;
    }

    /* 当前位置属于哪一页（取最近的页位置） */
    _stopIndex(left, stops) {
      var best = 0;
      var bestD = Infinity;
      for (var i = 0; i < stops.length; i++) {
        var d = Math.abs(left - stops[i]);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    }

    /* 一页 = 一张卡（卡宽 + 轨道间距） */
    _step(rail) {
      var item = rail.querySelector('.vl-hpc__item');
      var w = item ? item.getBoundingClientRect().width : rail.clientWidth * 0.8;
      return w + this._gap(rail);
    }

    _gap(rail) {
      if (typeof window.getComputedStyle !== 'function') return 0;
      var css = window.getComputedStyle(rail);
      var gap = parseFloat(css.columnGap || css.gap);
      return isNaN(gap) || gap < 0 ? 0 : gap;
    }

    /* 缓动滚动（与 05 同款自写缓动）；期间关掉 scroll-snap，避免与吸附抢位置 */
    _scrollTo(rail, left, instant) {
      var self = this;
      if (this._scrollRaf) {
        window.cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = 0;
      }
      var start = rail.scrollLeft;
      var distance = left - start;
      if (Math.abs(distance) < 1) return;
      if (instant) {
        rail.scrollLeft = left;
        this._syncAllNav();
        return;
      }
      if (typeof window.requestAnimationFrame !== 'function') {
        rail.scrollLeft = left;
        return;
      }
      rail.style.scrollSnapType = 'none';
      var t0 = 0;
      var step = function (ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / PAGE_MS);
        var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        rail.scrollLeft = start + distance * eased;
        if (p < 1) {
          self._scrollRaf = window.requestAnimationFrame(step);
        } else {
          self._scrollRaf = 0;
          rail.style.scrollSnapType = '';
          self._syncAllNav();
        }
      };
      this._scrollRaf = window.requestAnimationFrame(step);
    }
  }

  if (!customElements.get('vl-home-product-cards')) {
    customElements.define('vl-home-product-cards', VLHomeProductCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleInfoMarquees(document);
    });
  } else {
    scheduleInfoMarquees(document);
  }
  window.addEventListener('load', function () {
    scheduleInfoMarquees(document);
  });
  window.addEventListener('resize', function () {
    scheduleInfoMarquees(document);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      scheduleInfoMarquees(document);
    });
  }

  window.VLHomeProductCards = VLHomeProductCards;
})();
